import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Button, Table, Form, Row, Col, Badge, Modal } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  useGetSendPoolsQuery,
  useGetDomainsQuery,
  useGetEmailAccountsQuery,
  useLazyGetSendPoolEndpointsQuery,
  useLazyGetDomainRoutingPolicyQuery,
  useSetDomainFreezeUntilMutation,
  useSetSendPoolHealthMutation,
  useCreateSendPoolMutation,
  useUpdateSendPoolMutation,
  useDeleteSendPoolMutation,
  useGetUserPreferencesQuery,
} from '../services/api';
import EMMSectionHeader from './EMMSectionHeader';
import HelpSidebar from './HelpSidebar';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleStringInTimezone,
} from '../utils/tenantTimezone';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';
import { getDomainPickerLabel } from '../utils/relayAccountLabel';

type HealthState = 'GREEN' | 'YELLOW' | 'RED';
type MessageClass = 'cold_outreach' | 'transactional' | 'newsletter';
type CreatePurpose = 'cold_outreach' | 'transactional' | 'newsletter';
type CreateProvider = 'SMTP_COM' | 'MAILCHANNELS' | 'MAILGUN' | 'SENDGRID' | 'ELASTIC_EMAIL';

function healthVariant(state?: string) {
  if (state === 'GREEN') return 'success';
  if (state === 'YELLOW') return 'warning';
  if (state === 'RED') return 'danger';
  return 'secondary';
}

function normalizeDomainValue(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function domainMatchesPoolDomain(accountDomainRaw: string, poolDomainRaw: string): boolean {
  const accountDomain = normalizeDomainValue(accountDomainRaw);
  const poolDomain = normalizeDomainValue(poolDomainRaw);
  if (!accountDomain || !poolDomain) return false;
  if (accountDomain === poolDomain) return true;
  if (accountDomain.endsWith(`.${poolDomain}`)) return true;
  const accountRootDomain = accountDomain.split('.').slice(-2).join('.');
  const poolRootDomain = poolDomain.split('.').slice(-2).join('.');
  return Boolean(accountRootDomain && poolRootDomain && accountRootDomain === poolRootDomain);
}

export default function SendPoolsAdmin() {
  const [error, setError] = useState<string | null>(null);
  const [expandedPoolId, setExpandedPoolId] = useState<string | null>(null);
  const [endpointsByPoolId, setEndpointsByPoolId] = useState<Record<string, any[]>>({});

  const [freezeDomain, setFreezeDomain] = useState('');
  const [freezeMessageClass, setFreezeMessageClass] = useState<MessageClass>('cold_outreach');
  const [freezeUntil, setFreezeUntil] = useState<string>(''); // ISO string

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createProvider, setCreateProvider] = useState<CreateProvider>('SMTP_COM');
  const [createPurpose, setCreatePurpose] = useState<CreatePurpose>('transactional');

  const [editingPoolId, setEditingPoolId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [showHelpSidebar, setShowHelpSidebar] = useState(false);
  const [activeFreezes, setActiveFreezes] = useState<Array<{ domain: string; messageClass: string; freezeUntil: string }>>([]);
  const [routingPoliciesByDomain, setRoutingPoliciesByDomain] = useState<Record<string, any>>({});
  const [freezesVersion, setFreezesVersion] = useState(0);

  const expandedEndpoints = useMemo(
    () => (expandedPoolId ? endpointsByPoolId[expandedPoolId] || [] : []),
    [expandedPoolId, endpointsByPoolId]
  );

  const {
    data: sendPoolsData,
    error: sendPoolsError,
    isLoading: isLoadingPools,
    refetch: refreshPools,
  } = useGetSendPoolsQuery(undefined);
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );

  const { data: domainsData } = useGetDomainsQuery(undefined);
  const { data: emailAccountsData } = useGetEmailAccountsQuery(undefined);
  const domainOptions = useMemo(() => {
    const raw = (domainsData as any)?.data?.domains ?? (domainsData as any)?.domains ?? [];
    const deduped = new Map<string, { value: string; label: string }>();
    (Array.isArray(raw) ? raw : []).forEach((item: any) => {
      const value = String(item?.domain ?? item ?? '').trim();
      if (!value) return;
      const label = getDomainPickerLabel(item);
      deduped.set(value, { value, label: label || value });
    });
    return Array.from(deduped.values()).sort((a, b) =>
      a.value.toLowerCase().localeCompare(b.value.toLowerCase())
    );
  }, [domainsData]);
  const domainNames = useMemo(() => domainOptions.map((option) => option.value), [domainOptions]);
  const domainNamesDependency = useMemo(() => domainNames.join(','), [domainNames]);

  const pools = (sendPoolsData?.data?.pools || []) as any[];

  const [fetchEndpoints] = useLazyGetSendPoolEndpointsQuery();
  const [fetchRouting] = useLazyGetDomainRoutingPolicyQuery();
  const [setSendPoolHealth, { isLoading: isSettingHealth }] = useSetSendPoolHealthMutation();
  const [setDomainFreezeUntil, { isLoading: isSettingFreeze }] = useSetDomainFreezeUntilMutation();
  const [createSendPool, { isLoading: isCreatingPool }] = useCreateSendPoolMutation();
  const [updateSendPool, { isLoading: isUpdatingPool }] = useUpdateSendPoolMutation();
  const [deleteSendPool, { isLoading: isDeletingPool }] = useDeleteSendPoolMutation();

  useEffect(() => {
    if (!sendPoolsError) return;
    const msg =
      // RTK Query error shape (fetchBaseQuery)
      (sendPoolsError as any)?.data?.message ||
      (sendPoolsError as any)?.error ||
      'Failed to load pools';
    setError(String(msg));
  }, [sendPoolsError]);

  const refreshFreezes = useCallback(() => setFreezesVersion((v: number) => v + 1), []);

  useEffect(() => {
    if (domainNames.length === 0) {
      setActiveFreezes([]);
      return;
    }
    let cancelled = false;
    const now = Date.now();
    Promise.all(
      domainNames.map((domain) =>
        fetchRouting(domain)
          .unwrap()
          .then((res: any) => ({ domain, policy: res?.data?.policy ?? res?.policy }))
      )
    )
      .then((results) => {
        if (cancelled) return;
        const list: Array<{ domain: string; messageClass: string; freezeUntil: string }> = [];
        const routingMap: Record<string, any> = {};
        for (const { domain, policy } of results) {
          routingMap[domain] = policy || null;
          const routes = policy?.routes || {};
          for (const [messageClass, route] of Object.entries(routes as Record<string, { freezeUntil?: string }>)) {
            const until = route?.freezeUntil;
            if (until && !Number.isNaN(Date.parse(until)) && Date.parse(until) > now) {
              list.push({ domain, messageClass, freezeUntil: until });
            }
          }
        }
        list.sort((a, b) => a.domain.localeCompare(b.domain) || a.messageClass.localeCompare(b.messageClass));
        setActiveFreezes(list);
        setRoutingPoliciesByDomain(routingMap);
      })
      .catch(() => {
        if (!cancelled) {
          setActiveFreezes([]);
          setRoutingPoliciesByDomain({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [domainNamesDependency, domainNames, freezesVersion, fetchRouting]);

  async function toggleExpand(poolId: string) {
    if (expandedPoolId === poolId) {
      setExpandedPoolId(null);
      return;
    }
    setExpandedPoolId(poolId);
    if (!endpointsByPoolId[poolId]) {
      try {
        const res: any = await fetchEndpoints(poolId).unwrap();
        const endpoints = res?.data?.endpoints || [];
        setEndpointsByPoolId((prev) => ({ ...prev, [poolId]: endpoints }));
      } catch (e: any) {
        const msg = e?.data?.message || e?.message || e?.error || 'Failed to load endpoints';
        setError(String(msg));
      }
    }
  }

  async function setHealth(poolId: string, state: HealthState) {
    setError(null);
    try {
      await setSendPoolHealth({ poolId, state }).unwrap();
      await refreshPools();
    } catch (e: any) {
      const msg = e?.data?.message || e?.message || e?.error || 'Failed to update health';
      setError(String(msg));
    }
  }

  async function applyFreeze() {
    setError(null);
    try {
      const value = freezeUntil.trim() === '' ? null : freezeUntil.trim();
      await setDomainFreezeUntil({
        domain: freezeDomain.trim(),
        messageClass: freezeMessageClass,
        freezeUntil: value,
      }).unwrap();
      refreshFreezes();
    } catch (e: any) {
      const msg = e?.data?.message || e?.message || e?.error || 'Failed to set freezeUntil';
      setError(String(msg));
    }
  }

  async function unfreeze(domain: string, messageClass: string) {
    setError(null);
    try {
      await setDomainFreezeUntil({ domain, messageClass, freezeUntil: null }).unwrap();
      refreshFreezes();
    } catch (e: any) {
      const msg = e?.data?.message || e?.message || e?.error || 'Failed to unfreeze';
      setError(String(msg));
    }
  }

  function openCreateModal() {
    setCreateName('');
    setCreateProvider('SMTP_COM');
    setCreatePurpose('transactional');
    setError(null);
    setShowCreateModal(true);
  }

  async function submitCreatePool() {
    const name = createName.trim();
    if (!name) {
      setError('Name is required');
      return;
    }
    setError(null);
    try {
      // Backend expects cold_outreach | business_emails; transactional/newsletter both map to business_emails
      const purposeForApi = createPurpose === 'cold_outreach' ? 'cold_outreach' : 'business_emails';
      await createSendPool({
        name,
        providerType: createProvider,
        purpose: purposeForApi,
      }).unwrap();
      setShowCreateModal(false);
      refreshPools();
    } catch (e: any) {
      const msg = e?.data?.message || e?.message || e?.error || 'Failed to create pool';
      setError(String(msg));
    }
  }

  function openEditModal(p: { poolId: string; name: string }) {
    setEditingPoolId(p.poolId);
    setEditName(p.name);
    setError(null);
  }

  async function submitEditPool() {
    if (!editingPoolId) return;
    const name = editName.trim();
    if (!name) {
      setError('Name is required');
      return;
    }
    setError(null);
    try {
      await updateSendPool({ poolId: editingPoolId, name }).unwrap();
      setEditingPoolId(null);
      refreshPools();
    } catch (e: any) {
      const msg = e?.data?.message || e?.message || e?.error || 'Failed to update pool name';
      setError(String(msg));
    }
  }

  async function handleDeletePool(poolId: string, poolName: string) {
    if (!window.confirm(`Delete pool "${poolName}" (${poolId})? This cannot be undone.`)) return;
    setError(null);
    try {
      await deleteSendPool(poolId).unwrap();
      if (expandedPoolId === poolId) setExpandedPoolId(null);
      refreshPools();
    } catch (e: any) {
      const msg = e?.data?.message || e?.message || e?.error || 'Failed to delete pool';
      const domains = e?.data?.details?.domains;
      const displayMsg =
        Array.isArray(domains) && domains.length > 0
          ? `This pool is still in use by these domains: ${domains.join(', ')}. Change or remove outbound routing for these domains first.`
          : String(msg);
      setError(displayMsg);
    }
  }

  const loading = isLoadingPools || isSettingHealth || isSettingFreeze || isCreatingPool || isUpdatingPool || isDeletingPool;
  const poolEmailAccountCounts = useMemo(() => {
    const countsByPoolId: Record<string, number> = {};
    const emailAccounts =
      (emailAccountsData as any)?.data?.emailAccounts ??
      (emailAccountsData as any)?.emailAccounts ??
      [];
    const normalizedAccountDomains = (Array.isArray(emailAccounts) ? emailAccounts : [])
      .map((account: any) => normalizeDomainValue(account?.domain || account?.email?.split('@')?.[1] || ''))
      .filter(Boolean);

    const poolDomains = new Map<string, Set<string>>();
    domainNames.forEach((domainName) => {
      const policy = routingPoliciesByDomain[domainName];
      if (!policy || typeof policy !== 'object') return;

      const referencedPoolIds = new Set<string>();
      const forcedPoolId = String(policy?.overrides?.forcePoolId || '').trim();
      if (forcedPoolId) referencedPoolIds.add(forcedPoolId);

      const routes = policy?.routes && typeof policy.routes === 'object' ? policy.routes : {};
      Object.values(routes as Record<string, any>).forEach((route: any) => {
        const primary = String(route?.primaryPoolId || '').trim();
        if (primary) referencedPoolIds.add(primary);
      });

      referencedPoolIds.forEach((poolId) => {
        const domainsForPool = poolDomains.get(poolId) || new Set<string>();
        domainsForPool.add(domainName);
        poolDomains.set(poolId, domainsForPool);
      });
    });

    pools.forEach((pool: any) => {
      const poolId = String(pool?.poolId || '');
      if (poolId) countsByPoolId[poolId] = 0;
    });

    Array.from(poolDomains.entries()).forEach(([poolId, domainsSet]) => {
      const routedDomains = Array.from(domainsSet).map((d) => normalizeDomainValue(d)).filter(Boolean);
      if (routedDomains.length === 0) {
        countsByPoolId[poolId] = 0;
        return;
      }
      const count = normalizedAccountDomains.reduce((total, accountDomain) => {
        const isUsingPool = routedDomains.some((domain) =>
          domainMatchesPoolDomain(accountDomain, domain)
        );
        return total + (isUsingPool ? 1 : 0);
      }, 0);
      countsByPoolId[poolId] = count;
    });

    return countsByPoolId;
  }, [emailAccountsData, domainNames, routingPoliciesByDomain, pools]);
  const formatFreezeUntil = useCallback(
    (value: string) => {
      const formatted = toLocaleStringInTimezone(
        value,
        'en-US',
        { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' },
        tenantTimezone
      );
      return formatted || value;
    },
    [tenantTimezone]
  );

  return (
    <>
      <EMMSectionHeader title="Send Pools" showHelp />
      <div className="pt-3">
      {/* Action bar - same spot and size as Clients "+ Client" button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={openCreateModal}
            disabled={loading}
          >
            Add pool
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={refreshPools}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>
      <Row className="mb-3">
        <Col>
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>
            Live ops controls (health + freezeUntil) — no redeploys required.
          </div>
        </Col>
      </Row>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add send pool</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              placeholder="e.g. Transactional Primary"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Provider</Form.Label>
            <Form.Select
              value={createProvider}
              onChange={(e) => setCreateProvider(e.target.value as CreateProvider)}
            >
              <option value="SMTP_COM">SMTP.com</option>
              <option value="MAILCHANNELS">MailChannels</option>
              <option value="MAILGUN">Mailgun</option>
              <option value="SENDGRID">Twilio SendGrid</option>
              <option value="ELASTIC_EMAIL">Elastic Email</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Purpose</Form.Label>
            <Form.Select
              value={createPurpose}
              onChange={(e) => setCreatePurpose(e.target.value as CreatePurpose)}
            >
              <option value="cold_outreach">Cold Outreach</option>
              <option value="transactional">Transactional</option>
              <option value="newsletter">Newsletter</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Transactional and Newsletter pools for opted-in mail; Cold Outreach for cold outreach only.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitCreatePool} disabled={isCreatingPool || !createName.trim()}>
            {isCreatingPool ? 'Creating…' : 'Create pool'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={editingPoolId !== null} onHide={() => setEditingPoolId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit pool name</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-0">
            <Form.Label>Name</Form.Label>
            <Form.Control
              placeholder="e.g. Transactional Primary"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingPoolId(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitEditPool} disabled={isUpdatingPool || !editName.trim()}>
            {isUpdatingPool ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {error && (
        <Card className="mb-3 border-danger">
          <Card.Body style={{ color: '#b00020' }}>{error}</Card.Body>
        </Card>
      )}

      <Card className="mb-3">
        <Card.Header>Freeze a primary route (DomainRoutingPolicy.routes[*].freezeUntil)</Card.Header>
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label>Domain</Form.Label>
              <Form.Select
                value={freezeDomain}
                onChange={(e) => setFreezeDomain(e.target.value)}
                aria-label="Select domain"
              >
                <option value="">Select domain</option>
                {domainOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Message Class</Form.Label>
              <Form.Select value={freezeMessageClass} onChange={(e) => setFreezeMessageClass(e.target.value as MessageClass)}>
                <option value="cold_outreach">cold_outreach</option>
                <option value="transactional">transactional</option>
                <option value="newsletter">newsletter</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>
                freezeUntil (pick date; clear to unfreeze)
                {tenantTimezone ? ` - shown as ${tenantTimezone}` : ''}
              </Form.Label>
              <DatePicker
                selected={freezeUntil ? (() => {
                  const d = new Date(freezeUntil);
                  return Number.isNaN(d.getTime()) ? null : d;
                })() : null}
                onChange={(date: Date | null) => setFreezeUntil(date ? date.toISOString() : '')}
                showTimeSelect
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="MMM d, yyyy h:mm aa"
                placeholderText="Pick date & time"
                isClearable
                className="form-control"
                calendarClassName="send-pools-freeze-calendar"
              />
            </Col>
            <Col md={1}>
              <Button variant="outline-primary" onClick={applyFreeze} disabled={loading || !freezeDomain.trim()}>
                Set
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Active freezes</Card.Header>
        <Card.Body>
          {activeFreezes.length === 0 ? (
            <div className="text-muted small">No active freezes. Set a freeze above to pause a primary route until a date.</div>
          ) : (
            <Table responsive size="sm" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Message class</th>
                  <th>freezeUntil</th>
                  <th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {activeFreezes.map((f, idx) => (
                  <tr key={`${f.domain}-${f.messageClass}-${idx}`}>
                    <td>{f.domain}</td>
                    <td>{f.messageClass}</td>
                    <td>{formatFreezeUntil(f.freezeUntil)}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => unfreeze(f.domain, f.messageClass)}
                        disabled={loading}
                      >
                        Unfreeze
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Pools</Card.Header>
        <Card.Body style={{ padding: 0 }}>
          <Table responsive hover size="sm" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Pool</th>
                <th>Provider</th>
                <th>Status</th>
                <th>Health</th>
                <th>Email Accounts Using</th>
                <th style={{ width: 360 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pools.map((p: any) => {
                const state = p?.health?.state as string | undefined;
                return (
                  <React.Fragment key={p.poolId}>
                    <tr>
                      <td>
                        <div style={{ fontWeight: 600 }}>{p.name}</div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>{p.poolId}</div>
                      </td>
                      <td>{p.providerType}</td>
                      <td>{p.status}</td>
                      <td>
                        <Badge bg={healthVariant(state)}>{state || 'UNKNOWN'}</Badge>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{poolEmailAccountCounts[String(p.poolId || '')] ?? 0}</div>
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap align-items-center">
                          <Button size="sm" variant="outline-success" onClick={() => setHealth(p.poolId, 'GREEN')} disabled={loading} style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}>
                            GREEN
                          </Button>
                          <Button size="sm" variant="outline-warning" onClick={() => setHealth(p.poolId, 'YELLOW')} disabled={loading} style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}>
                            YELLOW
                          </Button>
                          <Button size="sm" variant="outline-danger" onClick={() => setHealth(p.poolId, 'RED')} disabled={loading} style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}>
                            RED
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => toggleExpand(p.poolId)} disabled={loading} style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}>
                            {expandedPoolId === p.poolId ? 'Hide' : 'Endpoints'}
                          </Button>
                          <Button size="sm" variant="outline-primary" onClick={() => openEditModal({ poolId: p.poolId, name: p.name })} disabled={loading} title="Edit name" style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}>
                            Edit
                          </Button>
                          <Button size="sm" variant="outline-danger" onClick={() => handleDeletePool(p.poolId, p.name)} disabled={loading} title="Delete pool" style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}>
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expandedPoolId === p.poolId && (
                      <tr>
                        <td colSpan={6}>
                          <div style={{ padding: '8px 6px' }}>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>Endpoints</div>
                            {expandedEndpoints.length === 0 ? (
                              <div style={{ color: '#666' }}>No endpoints found.</div>
                            ) : (
                              <Table responsive size="sm" bordered>
                                <thead>
                                  <tr>
                                    <th>endpointId</th>
                                    <th>type</th>
                                    <th>status</th>
                                    <th>priority</th>
                                    <th>details</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {expandedEndpoints.map((e) => (
                                    <tr key={e.endpointId}>
                                      <td>{e.endpointId}</td>
                                      <td>{e.endpointType}</td>
                                      <td>{e.status}</td>
                                      <td>{e.priority ?? ''}</td>
                                      <td style={{ fontSize: '0.85rem' }}>
                                        {e.endpointType === 'SMTP' ? (
                                          <span>{e.smtp?.host}:{e.smtp?.port} ({e.smtp?.security || 'STARTTLS'})</span>
                                        ) : (
                                          <span>{e.api?.baseUrl}</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <HelpSidebar
        pageKey="send-pools"
        isOpen={showHelpSidebar}
        onToggle={() => setShowHelpSidebar(!showHelpSidebar)}
      />
    </div>
    </>
  );
}


